import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Pencil, Trash2, MapPin, Phone, Store, ChevronRight, Lock } from 'lucide-react';
import DeleteConfirmModal from './DeleteConfirmModal';
import { usePermissionCheck } from '../../hooks/permissionHooks/usePermissionCheck';
import { PERMISSIONS } from '../../config/permissions';

const BranchCard = ({ branch }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="p-4 bg-white rounded-xl border border-gray-100 transition-all duration-200 hover:border-pink-100 hover:shadow-md"
  >
    <div className="flex gap-3 items-start">
      <div className="w-10 h-10 rounded-lg bg-[#FF6C6C]/10 flex items-center justify-center shrink-0">
        <Store className="w-5 h-5 text-[#FF6C6C]" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">{branch.name}</h4>
        <div className="mt-2 space-y-2 text-sm">
          <div className="flex gap-2 items-center text-gray-600">
            <MapPin className="w-4 h-4 text-[#FF6C6C]" />
            <span>{branch.location}, {branch.region}</span>
          </div>
          <div className="flex gap-2 items-center text-gray-600">
            <Phone className="w-4 h-4 text-[#FF6C6C]" />
            <span>{branch.phone_number}</span>
          </div>
          {(branch.district || branch.suburb || branch.landmark || branch.gpsAddress) && (
            <div className="pt-2 mt-2 border-t border-gray-200 border-dashed">
              {branch.district && (
                <p className="text-gray-500">District: {branch.district}</p>
              )}
              {branch.suburb && (
                <p className="text-gray-500">Suburb: {branch.suburb}</p>
              )}
              {branch.landmark && (
                <p className="text-gray-500">Landmark: {branch.landmark}</p>
              )}
              {branch.gpsAddress && (
                <p className="text-gray-500">GPS: {branch.gpsAddress}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

const PartnerCard = ({ partner, onEdit, onDelete, isDeleting, canEdit, canDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { isAuthenticated } = usePermissionCheck();

  const handleEdit = () => {
    if (canEdit) {
      onEdit(partner);
    }
  };

  const handleDelete = () => {
    if (canDelete) {
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = () => {
    onDelete(partner);
  };

  // Don't render if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <motion.div
        layout
        className="overflow-hidden bg-gradient-to-br from-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 to-gray-50/50 hover:shadow-md hover:border-pink-100"
      >
        {/* Partner Header */}
        <div className="p-6">
          <div className="flex gap-4 items-start">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] flex items-center justify-center shrink-0 shadow-lg">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-4 justify-between sm:flex-row sm:items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 truncate bg-gradient-to-r from-[#FF6C6C] to-pink-600 bg-clip-text text-transparent">
                    {partner.name}
                  </h3>
                  <div className="flex flex-wrap gap-2 items-center mt-1 text-sm text-gray-600">
                    <span className="px-2 py-1 bg-[#FF6C6C]/10 rounded-lg text-[#FF6C6C] font-medium">
                      {partner.branches.length} {partner.branches.length === 1 ? 'Branch' : 'Branches'}
                    </span>
                    <span className="w-1 h-1 bg-gray-300 rounded-full" />
                    <span className="text-gray-500">
                      {Array.from(new Set(partner.branches.map(b => b.region))).join(', ')}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  {/* Edit Button - Permission Aware */}
                  {canEdit ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleEdit}
                      className="p-2.5 rounded-xl bg-[#FF6C6C]/10 hover:bg-[#FF6C6C]/20 transition-colors group"
                      title="Edit Partner"
                    >
                      <Pencil className="w-5 h-5 text-[#FF6C6C] group-hover:scale-110 transition-transform" />
                    </motion.button>
                  ) : (
                    <div
                      className="p-2.5 rounded-xl bg-gray-100 opacity-50 cursor-not-allowed"
                      title="You don't have permission to edit partners"
                    >
                      <Lock className="w-5 h-5 text-gray-400" />
                    </div>
                  )}

                  {/* Delete Button - Permission Aware */}
                  {canDelete ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleDelete}
                      className="p-2.5 rounded-xl bg-red-50 hover:bg-red-100 transition-colors group"
                      title="Delete Partner"
                    >
                      <Trash2 className="w-5 h-5 text-red-500 transition-transform group-hover:scale-110" />
                    </motion.button>
                  ) : (
                    <div
                      className="p-2.5 rounded-xl bg-gray-100 opacity-50 cursor-not-allowed"
                      title="You don't have permission to delete partners"
                    >
                      <Lock className="w-5 h-5 text-gray-400" />
                    </div>
                  )}

                  {/* View Branches Button - Always Available */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors flex items-center gap-1 pr-3"
                    title={`${isExpanded ? 'Hide' : 'Show'} Partner Branches`}
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {isExpanded ? 'Hide' : 'Show'} Branches
                    </span>
                    <motion.div
                      animate={{ rotate: isExpanded ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </motion.div>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>

          {/* Permission Status Indicator */}
          {(!canEdit || !canDelete) && (
            <div className="p-3 mt-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2 items-center">
                <Lock className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-800">
                  {!canEdit && !canDelete 
                    ? "View-only access: You cannot edit or delete this partner"
                    : !canEdit 
                      ? "Limited access: You cannot edit this partner"
                      : "Limited access: You cannot delete this partner"
                  }
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Branches Grid */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 pb-6"
            >
              <div className="pt-4 border-t border-gray-100">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {partner.branches.map(branch => (
                    <BranchCard key={branch.branch_slug} branch={branch} />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {showDeleteModal && canDelete && (
          <DeleteConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            partnerName={partner.name}
            isDeleting={isDeleting}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PartnerCard;